import type { FC } from 'react'
import { memo, useContext } from 'react'

import {
  AsyncSelect,
  type IAsyncSelectProps,
  IconButton,
  IconEdit,
  Toolbar,
} from '@procurify/ui'
import { ModalContext } from '@procurify/ui'
import { type FormikHelpers as FormikActions } from 'formik'
import { useIntl } from 'react-intl'

import { SavedFilterAPI } from '@webapp/api/SavedFilterAPI'
import { type SavedFilterCollection } from '@webapp/hooks/useSavedFilterManager/types'
import { type SavedFilter } from '@webapp/models/SavedFilter'
import { parseAPIError } from '@webapp/utils/errors'
import messages from './messages'
import { type FilterManagerFilterTag } from '../FilterManager/types'
import { toast } from '../Notification'
import { type SavedFiltersFormFormSchema } from '../SavedFiltersForm/SavedFiltersForm'
import { SavedFiltersFormModal } from '../SavedFiltersForm/SavedFiltersFormModal'

export interface ISavedFilterSelectProps extends IAsyncSelectProps<SavedFilter> {
  collection: SavedFilterCollection
  filterTags?: FilterManagerFilterTag<any>[]
  onEdit?: () => void
}

export const SavedFilterSelect: FC<ISavedFilterSelectProps> = memo((props) => {
  const { collection, filterTags, value, onEdit, onChange, ...selectProps } =
    props

  const intl = useIntl()

  const modalContext = useContext(ModalContext)

  const loadOptions: IAsyncSelectProps<SavedFilter>['loadOptions'] = (
    inputValue,
    pagination,
    callback
  ) => {
    SavedFilterAPI.getCollection(collection, {
      page_size: 'all',
      search: inputValue,
    })
      .then((res) => {
        callback({
          options: res.data,
          pagination: {
            hasMore: false,
            page: 1,
          },
        })
      })
      .catch(() => null)
  }

  const saveFilters = (id: SavedFilter['id'], newSavedFilter: SavedFilter) => {
    return SavedFilterAPI.editFilter(id, newSavedFilter).then((res) => res.data)
  }

  const deleteFilter = (id: SavedFilter['id']) => {
    return SavedFilterAPI.deleteFilter(id)
  }

  const showEditModal = (savedFilter: SavedFilter) => {
    modalContext.showModal(SavedFiltersFormModal, {
      savedFilter,
      filters: filterTags,
      open: true,
      onDelete: () => {
        deleteFilter(savedFilter.id)
          .then(() => {
            onChange(null, null)

            modalContext.hideModal()

            toast.success({
              title: intl.formatMessage(messages.notificationEditSuccessTitle),
              children: intl.formatMessage(
                messages.notificationDeleteSuccessMessage
              ),
            })
          })
          .catch((err) => {
            toast.error({
              title: intl.formatMessage(messages.notificationEditErrorTitle),
              children: parseAPIError(err.data).message,
            })
          })
      },
      onSubmit: (
        formValue: SavedFiltersFormFormSchema,
        action: FormikActions<SavedFiltersFormFormSchema>
      ) => {
        action.setSubmitting(true)

        saveFilters(savedFilter.id, {
          ...savedFilter,
          name: formValue.name,
        })
          .then((newFilter) => {
            action.setSubmitting(false)

            onChange(newFilter, null)

            modalContext.hideModal()

            toast.success({
              title: intl.formatMessage(messages.notificationEditSuccessTitle),
              children: intl.formatMessage(
                messages.notificationEditSuccessMessage
              ),
            })
          })
          .catch((err) => {
            action.setSubmitting(false)

            toast.error({
              title: intl.formatMessage(messages.notificationEditErrorTitle),
              children: parseAPIError(err.data).message,
            })
          })
      },
    })
  }

  return (
    <Toolbar>
      <Toolbar.Item flexGrow={1}>
        <AsyncSelect<SavedFilter>
          value={value}
          placeholder='Select a saved filter'
          loadOptions={loadOptions}
          defaultOptions={true}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => (option.id ? option.id.toString() : null)}
          onChange={onChange}
          {...selectProps}
        />
      </Toolbar.Item>
      {value && (
        <Toolbar.Item pl={1}>
          <IconButton
            iconComponent={<IconEdit />}
            onClick={() => {
              showEditModal(value as SavedFilter)
              onEdit()
            }}
            aria-label={intl.formatMessage(messages.editButtonLabel)}
          />
        </Toolbar.Item>
      )}
    </Toolbar>
  )
})

SavedFilterSelect.defaultProps = {
  onEdit: () => undefined,
  onChange: () => undefined,
}
SavedFilterSelect.displayName = 'SavedFilterSelect'
